"use client";

import { useState, useCallback, useRef, useEffect } from "react";

// ─── Utilities ───

const TEMPO_MIN = 20;
const TEMPO_MAX = 300;

function clampTempo(v: number): number {
  return Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(v)));
}

function getTempoMarking(bpm: number): string {
  if (bpm <= 24) return "Larghissimo";
  if (bpm <= 40) return "Grave";
  if (bpm <= 60) return "Largo";
  if (bpm <= 70) return "Adagio";
  if (bpm <= 85) return "Andante";
  if (bpm <= 100) return "Moderato";
  if (bpm <= 120) return "Allegretto";
  if (bpm <= 140) return "Allegro";
  if (bpm <= 168) return "Vivace";
  if (bpm <= 200) return "Presto";
  return "Prestissimo";
}

// ─── Component ───

interface TempoControlProps {
  value: number;
  onChange: (v: number) => void;
}

export function TempoControl({ value, onChange }: TempoControlProps) {
  const [editText, setEditText] = useState(String(value));
  const [isEditing, setIsEditing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const tapsRef = useRef<number[]>([]);
  const tapResetRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const holdRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setEditText(String(value));
  }, [value, isEditing]);

  const step = useCallback(
    (delta: number) => {
      onChange(clampTempo(value + delta));
    },
    [value, onChange],
  );

  const accRef = useRef(value);
  accRef.current = value;

  const startHold = useCallback(
    (delta: number) => {
      let count = 0;
      holdRef.current = setInterval(() => {
        count++;
        const speed = count > 15 ? 10 : count > 5 ? 5 : 1;
        accRef.current = clampTempo(accRef.current + delta * speed);
        onChange(accRef.current);
      }, 80);
    },
    [onChange],
  );

  const stopHold = useCallback(() => {
    if (holdRef.current) clearInterval(holdRef.current);
  }, []);

  const handleTap = useCallback(() => {
    const now = performance.now();

    clearTimeout(tapResetRef.current);
    tapResetRef.current = setTimeout(() => {
      tapsRef.current = [];
    }, 2000);

    tapsRef.current.push(now);
    if (tapsRef.current.length > 8) tapsRef.current.shift();

    setTapPulse(true);
    setTimeout(() => setTapPulse(false), 150);

    if (tapsRef.current.length >= 2) {
      const intervals: number[] = [];
      for (let i = 1; i < tapsRef.current.length; i++) {
        intervals.push(tapsRef.current[i] - tapsRef.current[i - 1]);
      }
      const avgMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      onChange(clampTempo(60000 / avgMs));
    }
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        step(e.shiftKey ? 10 : 1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        step(e.shiftKey ? -10 : -1);
      } else if (e.key === "Enter") {
        (e.target as HTMLInputElement).blur();
      }
    },
    [step],
  );

  const commitEdit = useCallback(() => {
    setIsEditing(false);
    const v = parseInt(editText, 10);
    if (Number.isFinite(v)) {
      onChange(clampTempo(v));
    } else {
      setEditText(String(value));
    }
  }, [editText, value, onChange]);

  const sliderPercent = ((value - TEMPO_MIN) / (TEMPO_MAX - TEMPO_MIN)) * 100;

  const btnStyle = {
    backgroundColor: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
  };
  const btnHoverClass =
    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all duration-150 cursor-pointer select-none hover:border-[rgba(139,92,246,0.25)] hover:bg-[rgba(139,92,246,0.08)] active:bg-[rgba(139,92,246,0.15)]";

  return (
    <div className="flex flex-col gap-1.5 shrink-0">
      <label className="text-[11px] font-medium text-text-muted uppercase tracking-wider">
        Tempo
      </label>

      {/* Stepper row */}
      <div className="flex items-center gap-2">
        {/* Minus */}
        <button
          className={btnHoverClass}
          style={btnStyle}
          onClick={(e) => step(e.shiftKey ? -10 : -1)}
          onMouseDown={() => startHold(-1)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          title="-1 (Shift: -10)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#5c5672" strokeWidth="1.5" strokeLinecap="round">
            <path d="M3 7h8" />
          </svg>
        </button>

        {/* Editable value */}
        <div
          className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 relative"
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: isEditing ? "1px solid rgba(139,92,246,0.4)" : "1px solid rgba(255,255,255,0.06)",
            boxShadow: isEditing ? "0 0 0 2px rgba(139,92,246,0.1)" : "none",
            transition: "border-color 150ms, box-shadow 150ms",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={editText}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setEditText(raw);
            }}
            onFocus={() => {
              setIsEditing(true);
              setTimeout(() => inputRef.current?.select(), 0);
            }}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            className="w-12 bg-transparent text-center text-[13px] font-semibold text-text-primary tabular-nums outline-none"
          />
          <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider pointer-events-none">
            BPM
          </span>
        </div>

        {/* Plus */}
        <button
          className={btnHoverClass}
          style={btnStyle}
          onClick={(e) => step(e.shiftKey ? 10 : 1)}
          onMouseDown={() => startHold(1)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          title="+1 (Shift: +10)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#5c5672" strokeWidth="1.5" strokeLinecap="round">
            <path d="M7 3v8M3 7h8" />
          </svg>
        </button>

        {/* Tap tempo */}
        <button
          onClick={handleTap}
          className="h-9 px-3 rounded-lg flex items-center gap-1.5 shrink-0 transition-all duration-150 cursor-pointer select-none"
          style={{
            backgroundColor: "rgba(139,92,246,0.06)",
            border: "1px solid var(--color-violet-soft)",
            transform: tapPulse ? "scale(0.92)" : "scale(1)",
          }}
          title="Tape le rythme pour détecter le BPM"
        >
          <svg
            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-violet-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: tapPulse ? "scale(1.3)" : "scale(1)", transition: "transform 150ms ease" }}
          >
            <path d="M12 2v10" />
            <path d="M5.2 21h13.6L12 3 5.2 21z" />
            <path d="M16 8l-4 4" />
          </svg>
          <span className="text-[10px] font-semibold text-[var(--color-violet-text)] uppercase tracking-wider">
            Tap
          </span>
        </button>
      </div>

      {/* Italian marking */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] italic text-text-muted transition-opacity duration-200">
          {getTempoMarking(value)}
        </span>
        <span className="text-[10px] text-text-muted/50 tabular-nums">
          {TEMPO_MIN} – {TEMPO_MAX}
        </span>
      </div>

      {/* Slider */}
      <div className="relative h-3 flex items-center">
        <div
          className="absolute inset-x-0 h-[2px] rounded-full"
          style={{ backgroundColor: "rgba(255,255,255,0.06)" }}
        />
        <div
          className="absolute left-0 h-[2px] rounded-full"
          style={{
            width: `${sliderPercent}%`,
            background: "linear-gradient(90deg, var(--color-accent), var(--color-violet-text))",
          }}
        />
        <input
          type="range"
          min={TEMPO_MIN}
          max={TEMPO_MAX}
          step={1}
          value={value}
          onChange={(e) => onChange(clampTempo(parseInt(e.target.value, 10)))}
          className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-accent)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[rgba(16,16,22,0.98)] [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_rgba(139,92,246,0.3),0_2px_8px_rgba(0,0,0,0.4)] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150 [&::-webkit-slider-thumb]:hover:scale-125 [&::-webkit-slider-thumb]:active:scale-110
            [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[var(--color-accent)] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[rgba(16,16,22,0.98)] [&::-moz-range-thumb]:shadow-[0_0_0_2px_rgba(139,92,246,0.3),0_2px_8px_rgba(0,0,0,0.4)]
            [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-[2px]"
        />
      </div>
    </div>
  );
}
